import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { RoleEnum } from "@/services/moderators/generated/model";
import {
  getModeratorsRetrieveQueryKey,
  useModeratorsActivateCreate,
  useModeratorsDestroy,
  useModeratorsRetrieve,
  useModeratorsUpdate,
} from "@/services/moderators/generated/moderators-moderators/moderators-moderators";
import "./ModeratorPage.css";
import "./ModeratorProfilePage.css";

const ModeratorDetailPage = () => <ModeratorLayout>{(me) => <DetailGate me={me} />}</ModeratorLayout>;

interface MeShape {
  id: number;
  role: "MODERATOR" | "SUPERMODERATOR";
}

const DetailGate = ({ me }: { me: MeShape }) => {
  const { t } = useTranslation("moderator");
  const { id } = useParams<{ id: string }>();
  const moderatorId = Number(id);

  // Regular moderators can't manage teammates — but they can view their own
  // record, which lives at /moderator/profile. Bounce them there so the URL
  // never feels like a dead end.
  if (me.role !== "SUPERMODERATOR") {
    if (Number.isFinite(moderatorId) && moderatorId === me.id) {
      return <Navigate to="/moderator/profile" replace />;
    }
    return (
      <>
        <header className="mod-page__header">
          <div>
            <h1 className="mod-page__title">{t("shared.roleModerator")}</h1>
            <p className="mod-page__subtitle">{t("shared.superOnly")}</p>
          </div>
        </header>
        <div className="mod-page__empty">{t("shared.restricted")}</div>
      </>
    );
  }

  return <DetailContent currentModeratorId={me.id} />;
};

const DetailContent = ({ currentModeratorId }: { currentModeratorId: number }) => {
  const { t } = useTranslation(["moderator", "common"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const moderatorId = Number(id);
  const qc = useQueryClient();

  const {
    data: moderator,
    isLoading,
    error,
  } = useModeratorsRetrieve(moderatorId, {
    query: { enabled: Number.isFinite(moderatorId) },
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: getModeratorsRetrieveQueryKey(moderatorId) });

  const updateMutation = useModeratorsUpdate({ mutation: { onSuccess: invalidate } });
  const activateMutation = useModeratorsActivateCreate({ mutation: { onSuccess: invalidate } });
  const destroyMutation = useModeratorsDestroy({
    mutation: { onSuccess: () => navigate("/moderator/moderators") },
  });

  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState<typeof RoleEnum.MODERATOR | typeof RoleEnum.SUPERMODERATOR>(RoleEnum.MODERATOR);
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (moderator) {
      setFullName(moderator.fullName || "");
      setPhoneNumber(moderator.phoneNumber || "");
      setRole(moderator.role as typeof RoleEnum.MODERATOR | typeof RoleEnum.SUPERMODERATOR);
      setNewPassword("");
    }
  }, [moderator]);

  if (!Number.isFinite(moderatorId)) {
    return <div className="mod-page__error">{t("moderatorDetail.invalidId")}</div>;
  }
  if (isLoading) return <div className="mod-page__loading">{t("common:states.loading")}</div>;
  if (error || !moderator) return <div className="mod-page__error">{t("moderatorDetail.loadError")}</div>;

  const isSelf = moderator.id === currentModeratorId;

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => navigate(-1)}>
            ← {t("common:actions.back")}
          </button>
          <h1 className="mod-page__title">{moderator.fullName}</h1>
          <p className="mod-page__subtitle">{moderator.email}</p>
        </div>
      </header>

      <section className="mod-page__card">
        <h2 className="mod-page__card-title">{t("common:labels.status")}</h2>
        <p className="mod-page__card-meta">
          <span className={`mod-page__chip ${role === RoleEnum.SUPERMODERATOR ? "mod-page__chip--success" : ""}`}>
            {role === RoleEnum.SUPERMODERATOR ? t("shared.roleSuperModerator") : t("shared.roleModerator")}
          </span>
          <span
            className={`mod-page__chip ${moderator.isActive ? "mod-page__chip--success" : "mod-page__chip--danger"}`}
          >
            {moderator.isActive ? t("shared.active") : t("shared.inactive")}
          </span>
        </p>
        <div className="mod-page__form-row">
          <button
            type="button"
            className="mod-page__btn mod-page__btn--ghost"
            disabled={isSelf || activateMutation.isPending}
            onClick={() => activateMutation.mutate({ id: moderator.id, data: { isActive: !moderator.isActive } })}
          >
            {moderator.isActive ? t("shared.deactivate") : t("shared.activate")}
          </button>
          <button
            type="button"
            className="mod-page__btn mod-page__btn--danger"
            disabled={isSelf || destroyMutation.isPending}
            onClick={() => {
              if (window.confirm(t("shared.deleteModeratorConfirm", { name: moderator.fullName }))) {
                destroyMutation.mutate({ id: moderator.id });
              }
            }}
          >
            {t("common:actions.delete")}
          </button>
        </div>
        {isSelf ? <p className="mod-page__card-meta">{t("moderatorDetail.selfHint")}</p> : null}
      </section>

      <section className="mod-page__card">
        <h2 className="mod-page__card-title">{t("moderatorDetail.editProfile")}</h2>
        <form
          className="mod-profile__form"
          onSubmit={(e) => {
            e.preventDefault();
            setMessage(null);
            updateMutation.mutate(
              {
                id: moderator.id,
                data: {
                  fullName: fullName.trim(),
                  phoneNumber: phoneNumber.trim() || undefined,
                  role,
                  password: newPassword || undefined,
                },
              },
              {
                onSuccess: () => {
                  setMessage(t("moderatorDetail.saved"));
                  setNewPassword("");
                },
                onError: (err: unknown) => setMessage(extractError(err, t("shared.updateError"))),
              },
            );
          }}
        >
          <label>
            {t("shared.fullName")}
            <input
              className="mod-page__input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </label>
          <label>
            {t("shared.phone")}
            <input
              className="mod-page__input"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder={t("shared.optionalPlaceholder")}
            />
          </label>
          <label>
            {t("moderatorDetail.role")}
            <select
              className="mod-page__select"
              value={role}
              onChange={(e) => setRole(e.target.value as typeof RoleEnum.MODERATOR | typeof RoleEnum.SUPERMODERATOR)}
              disabled={isSelf}
            >
              <option value={RoleEnum.MODERATOR}>{t("shared.roleModerator")}</option>
              <option value={RoleEnum.SUPERMODERATOR}>{t("shared.roleSuperModerator")}</option>
            </select>
          </label>
          <label>
            {t("moderatorDetail.resetPassword")}
            <input
              type="password"
              className="mod-page__input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={newPassword ? 8 : undefined}
              autoComplete="new-password"
            />
          </label>
          <div className="mod-page__form-row">
            <button type="submit" className="mod-page__btn mod-page__btn--primary" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t("common:actions.saving") : t("common:actions.save")}
            </button>
            {message ? (
              <span className={message === t("moderatorDetail.saved") ? "mod-profile__ok" : "mod-page__inline-error"}>
                {message}
              </span>
            ) : null}
          </div>
        </form>
      </section>
    </div>
  );
};

function extractError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: Record<string, unknown> } } | null | undefined;
  const body = e?.response?.data;
  if (body) {
    if (typeof body.error === "string") return body.error;
    for (const v of Object.values(body)) {
      if (typeof v === "string") return v;
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
    }
  }
  return fallback;
}

export default ModeratorDetailPage;
